/**
 * Color-binding selector (VARIANTS_MVP follow-up).
 *
 * A single control in the SlotCard color row that replaces the old read-only
 * `$prop` chip AND the `↺` restore button. It shows the slot's current
 * baseColorFactor binding and opens a menu to:
 *   - rebind to any compatible color property (self + ancestors),
 *   - create a NEW color property and bind to it,
 *   - unbind (bake the current resolved color as a literal — "restore").
 *
 * All actions route through the shared trace-viewer editor helpers (one undo
 * entry each) and then refresh the panel. Menu open/close is React state and a
 * click-away listener — refs only, no DOM queries.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    listBindableColorProperties,
    bindRendererProperty,
    bindMaterialColorToProperty,
    unbindMaterialColor,
} from 'trace-viewer/editor';
import type { BindableColorProperty } from 'trace-viewer/editor';
import type { SceneObject } from 'trace-viewer/scene';

export interface ColorBindingSelectorProps {
    sceneObject: SceneObject;
    slotIndex: number;
    slotName: string;
    /** Property the slot's color is currently bound to, or null when unbound. */
    boundTo: string | null;
    /** Called after any bind/unbind so the panel re-reads and regens the preview. */
    onChanged: () => void;
}

const menuItem: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: '6px',
    padding: '4px 8px', fontSize: '11px', color: '#ccc',
    cursor: 'pointer', whiteSpace: 'nowrap', background: 'transparent',
    border: 'none', width: '100%', textAlign: 'left',
};

export const ColorBindingSelector: React.FC<ColorBindingSelectorProps> = ({
    sceneObject, slotIndex, slotName, boundTo, onChanged,
}) => {
    const [open, setOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);

    // Close on click-away.
    useEffect(() => {
        if (!open) return;
        const onDown = (e: MouseEvent): void => {
            if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
        };
        window.addEventListener('mousedown', onDown);
        return () => window.removeEventListener('mousedown', onDown);
    }, [open]);

    const after = useCallback((): void => { setOpen(false); onChanged(); }, [onChanged]);

    const rebind = (name: string): void => {
        if (name === boundTo) { setOpen(false); return; }
        void bindRendererProperty(sceneObject, name, { binding: 'materialColor', slot: slotIndex })
            .then(({ errors }) => {
                if (errors.length > 0) console.warn('[ColorBindingSelector] rebind failed:', errors);
                after();
            });
    };

    const createNew = (): void => {
        void bindMaterialColorToProperty(sceneObject, { slot: slotIndex, label: slotName, freeze: false })
            .then(({ errors }) => {
                if (errors.length > 0) console.warn('[ColorBindingSelector] create property failed:', errors);
                after();
            });
    };

    const unbind = (): void => {
        void unbindMaterialColor(sceneObject, { slot: slotIndex })
            .then(({ errors }) => {
                if (errors.length > 0) console.warn('[ColorBindingSelector] unbind failed:', errors);
                after();
            });
    };

    // Enumerated fresh each open (cheap, synchronous).
    const options: BindableColorProperty[] = open ? listBindableColorProperties(sceneObject) : [];

    return (
        <div ref={rootRef} style={{ position: 'relative', display: 'inline-flex' }}>
            <button
                onClick={() => setOpen(o => !o)}
                title={boundTo ? `Color bound to "${boundTo}" — click to rebind/unbind` : 'Bind color to a property'}
                style={{
                    fontSize: '9px',
                    color: boundTo ? '#b794f6' : '#888',
                    backgroundColor: boundTo ? '#b794f620' : 'transparent',
                    border: boundTo ? '1px solid #b794f640' : '1px solid #444',
                    padding: '1px 5px', borderRadius: '3px', cursor: 'pointer',
                    whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: '3px',
                }}
            >
                {boundTo ? `$${boundTo}` : 'Bind'}
                <span style={{ fontSize: '7px', opacity: 0.7 }}>▾</span>
            </button>

            {open && (
                <div
                    style={{
                        position: 'absolute', top: '100%', left: 0, marginTop: '3px', zIndex: 50,
                        minWidth: '140px', maxHeight: '220px', overflowY: 'auto',
                        backgroundColor: '#252525', border: '1px solid #444', borderRadius: '4px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.4)', padding: '3px 0',
                    }}
                >
                    {options.length === 0 && (
                        <div style={{ ...menuItem, color: '#666', cursor: 'default', fontStyle: 'italic' }}>
                            No color properties
                        </div>
                    )}
                    {options.map(opt => {
                        const active = opt.name === boundTo;
                        return (
                            <button
                                key={opt.name}
                                onClick={() => rebind(opt.name)}
                                style={{ ...menuItem, color: active ? '#fff' : '#ccc' }}
                                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#333')}
                                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                            >
                                <span style={{ width: '10px', color: '#b794f6' }}>{active ? '✓' : ''}</span>
                                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{opt.label}</span>
                                {opt.hasVariants && (
                                    <span style={{ fontSize: '8px', color: '#666' }} title="has variants">◈</span>
                                )}
                            </button>
                        );
                    })}

                    <div style={{ height: '1px', backgroundColor: '#3a3a3a', margin: '3px 0' }} />

                    <button
                        onClick={createNew}
                        style={menuItem}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#333')}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                        <span style={{ width: '10px', color: '#888' }}>+</span>
                        <span>New property</span>
                    </button>

                    {boundTo && (
                        <button
                            onClick={unbind}
                            style={{ ...menuItem, color: '#e88' }}
                            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#333')}
                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                        >
                            <span style={{ width: '10px' }} />
                            <span>Unbind (keep color)</span>
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};
